import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const ROLE_COLORS: Record<string, string> = {
  waitlist: '#6b7280',
  tester: '#2563eb',
  free: '#16a34a',
  premium: '#d97706',
  admin: '#dc2626',
};

const VALID_ROLES = ['waitlist', 'tester', 'free', 'premium', 'admin'];

interface WaitlistUser {
  id: number;
  email: string;
  role: string;
  notes: string | null;
  created_at: string;
  invited_at: string | null;
  confirmed: boolean;
}

function stadiuBadge(user: WaitlistUser): { label: string; color: string } {
  if (!user.confirmed) return { label: 'Neconfirmat', color: '#6b7280' };
  if (!user.invited_at) return { label: 'Confirmat', color: '#2563eb' };
  return { label: 'Invitat', color: '#d97706' };
}

const INVITE_SUBJECT = 'Ai primit acces la Flexara!';
const INVITE_MESSAGE =
  'Bună ziua,\n\nCererea ta de acces la Flexara a fost aprobată.\n\nÎți poți crea contul accesând linkul de mai jos:\nhttps://flexara.ro/login\n\nEchipa Flexara';

function UserCard({ user, onUpdate }: { user: WaitlistUser; onUpdate: (u: WaitlistUser) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(user.notes || '');
  const [notesSaved, setNotesSaved] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [emailStatus, setEmailStatus] = useState<'ok' | 'err' | null>(null);
  const [approving, setApproving] = useState(false);
  const [approveStatus, setApproveStatus] = useState<'ok' | null>(null);

  const changeRole = async (role: string) => {
    try {
      await api.patch(`/admin/waitlist/users/${user.id}/role`, { role });
      onUpdate({ ...user, role });
    } catch {}
  };

  const approveAndInvite = async () => {
    setApproving(true);
    try {
      await api.patch(`/admin/waitlist/users/${user.id}/role`, { role: 'tester' });
      await api.post(`/admin/waitlist/users/${user.id}/invite`, { subject: INVITE_SUBJECT, message: INVITE_MESSAGE });
      onUpdate({ ...user, role: 'tester', invited_at: new Date().toISOString() });
      setApproveStatus('ok');
      setTimeout(() => setApproveStatus(null), 3000);
    } catch {} finally {
      setApproving(false);
    }
  };

  const saveNotes = async () => {
    try {
      await api.patch(`/admin/waitlist/users/${user.id}/notes`, { notes });
      setNotesSaved(true);
      onUpdate({ ...user, notes });
      setTimeout(() => setNotesSaved(false), 2000);
    } catch {}
  };

  const sendInvite = async () => {
    setEmailStatus(null);
    try {
      await api.post(`/admin/waitlist/users/${user.id}/invite`, { subject, message });
      setEmailStatus('ok');
      onUpdate({ ...user, invited_at: new Date().toISOString() });
    } catch {
      setEmailStatus('err');
    }
    setTimeout(() => setEmailStatus(null), 3000);
  };

  const dateFormatted = user.created_at
    ? new Date(user.created_at).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const roleColor = ROLE_COLORS[user.role] || '#6b7280';
  const canSend = subject.trim().length > 0 && message.trim().length > 0;
  const stadiu = stadiuBadge(user);
  const showApproveBtn = user.role === 'waitlist' && user.confirmed;

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 10, background: '#fff', overflow: 'hidden' }}>
      <div
        onClick={() => setExpanded(v => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: '50%', background: roleColor, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 15, flexShrink: 0,
        }}>
          {user.email[0].toUpperCase()}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.email}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>{dateFormatted}</div>
        </div>

        {/* Stadiu badge */}
        <span style={{
          padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, flexShrink: 0,
          background: `${stadiu.color}18`, color: stadiu.color,
          border: `1px solid ${stadiu.color}40`,
        }}>
          {stadiu.label}
        </span>

        {/* Rol badge */}
        <span style={{
          padding: '2px 10px', borderRadius: 20,
          background: `${roleColor}20`, color: roleColor,
          fontSize: 12, fontWeight: 600, flexShrink: 0,
        }}>
          {user.role}
        </span>

        <span style={{ color: '#9ca3af', fontSize: 11 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid #f3f4f6', padding: 16, display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Aprobă și invită */}
          {showApproveBtn && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: 1, marginBottom: 8 }}>ACȚIUNE RAPIDĂ</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  onClick={approveAndInvite}
                  disabled={approving}
                  style={{
                    padding: '7px 18px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
                    cursor: approving ? 'not-allowed' : 'pointer',
                    background: approving ? '#e5e7eb' : '#16a34a',
                    color: approving ? '#9ca3af' : '#fff',
                  }}
                >
                  {approving ? 'Se procesează...' : 'Aprobă și invită'}
                </button>
                {approveStatus === 'ok' && <span style={{ fontSize: 12, color: '#16a34a' }}>Invitație trimisă!</span>}
              </div>
            </div>
          )}

          {/* Rol */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: 1, marginBottom: 8 }}>ROL</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {VALID_ROLES.map(r => (
                <button
                  key={r}
                  onClick={() => changeRole(r)}
                  style={{
                    padding: '4px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer', fontWeight: 600,
                    background: user.role === r ? `${ROLE_COLORS[r]}20` : '#f9fafb',
                    color: user.role === r ? ROLE_COLORS[r] : '#6b7280',
                    border: user.role === r ? `1.5px solid ${ROLE_COLORS[r]}` : '1.5px solid #e5e7eb',
                  }}
                >{r}</button>
              ))}
            </div>
          </div>

          {/* Note interne */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: 1, marginBottom: 8 }}>NOTE INTERNE</div>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Observații, sursa lead-ului, feedback din interviu..."
              style={{
                width: '100%', minHeight: 72, padding: '8px 10px', borderRadius: 8,
                border: '1px solid #e5e7eb', fontSize: 13, fontFamily: 'inherit',
                resize: 'vertical', boxSizing: 'border-box', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
              <button
                onClick={saveNotes}
                style={{
                  padding: '5px 14px', borderRadius: 8, border: 'none',
                  background: '#111827', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600,
                }}
              >Salvează note</button>
              {notesSaved && <span style={{ fontSize: 12, color: '#16a34a' }}>Note salvate</span>}
            </div>
          </div>

          {/* Trimite email */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: 1, marginBottom: 8 }}>TRIMITE EMAIL</div>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Subiect"
              style={{
                width: '100%', padding: '7px 10px', borderRadius: 8,
                border: '1px solid #e5e7eb', fontSize: 13, marginBottom: 8,
                boxSizing: 'border-box', outline: 'none',
              }}
            />
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Mesaj"
              style={{
                width: '100%', minHeight: 72, padding: '8px 10px', borderRadius: 8,
                border: '1px solid #e5e7eb', fontSize: 13, fontFamily: 'inherit',
                resize: 'vertical', boxSizing: 'border-box', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
              <button
                onClick={sendInvite}
                disabled={!canSend}
                style={{
                  padding: '5px 14px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
                  cursor: canSend ? 'pointer' : 'not-allowed',
                  background: canSend ? '#2563eb' : '#e5e7eb',
                  color: canSend ? '#fff' : '#9ca3af',
                }}
              >Trimite email</button>
              {emailStatus === 'ok' && <span style={{ fontSize: 12, color: '#16a34a' }}>Email trimis!</span>}
              {emailStatus === 'err' && <span style={{ fontSize: 12, color: '#dc2626' }}>Eroare la trimitere</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminWaitlist() {
  const [users, setUsers] = useState<WaitlistUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [view, setView] = useState<'pending' | 'all'>('pending');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/waitlist/users');
      setUsers(res.data);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const updateUser = (updated: WaitlistUser) =>
    setUsers(prev => prev.map(u => (u.id === updated.id ? updated : u)));

  const filtered = users.filter(u => {
    const matchSearch = u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === 'all' || u.role === filterRole;
    const matchView = view === 'all' || (u.confirmed && u.role === 'waitlist');
    return matchSearch && matchRole && matchView;
  });

  const roleCounts = VALID_ROLES.reduce<Record<string, number>>((acc, r) => {
    acc[r] = users.filter(u => u.role === r).length;
    return acc;
  }, {});

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 18px', borderRadius: 20, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', border: 'none',
    background: active ? '#111827' : '#f3f4f6',
    color: active ? '#fff' : '#6b7280',
  });

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 16px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>Waitlist · Flexara</h1>
          <span style={{ fontSize: 14, color: '#6b7280' }}>{users.length} utilizatori</span>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
          {VALID_ROLES.map(r => (
            <div key={r} style={{
              padding: '10px 16px', borderRadius: 10, background: '#fff',
              border: `1.5px solid ${ROLE_COLORS[r]}30`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80,
            }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: ROLE_COLORS[r] }}>{roleCounts[r]}</span>
              <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{r}</span>
            </div>
          ))}
        </div>

        {/* Tab toggle */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          <button style={tabStyle(view === 'pending')} onClick={() => setView('pending')}>
            În așteptare
          </button>
          <button style={tabStyle(view === 'all')} onClick={() => setView('all')}>
            Toți
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <input
            type="text"
            placeholder="Caută după email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 8,
              border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', background: '#fff',
            }}
          />
          <select
            value={filterRole}
            onChange={e => setFilterRole(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: 8,
              border: '1px solid #e5e7eb', fontSize: 14, background: '#fff',
              outline: 'none', cursor: 'pointer',
            }}
          >
            <option value="all">Toate rolurile</option>
            {VALID_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {/* List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Se încarcă...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Niciun utilizator găsit.</div>
        ) : (
          filtered.map(u => <UserCard key={u.id} user={u} onUpdate={updateUser} />)
        )}
      </div>
    </div>
  );
}
